import { getCurrentConnectionInfo } from 'portal-sdk';
import * as React from 'react';
import {
  VoiceboxConversationContext,
  VoiceboxSchemaContext,
  VoiceboxSettingsContext,
  useVoiceboxSettingsValue,
} from 'vet-bones/components/molecules/Voicebox';
import {
  DEFAULT_VOICEBOX_SETTINGS,
  VoiceboxApp,
  VoiceboxResponseMessage,
  VoiceboxSchema,
} from 'vet-bones/constants/voicebox';
import { copyToClipboard, getIsClipboardEnabled, noop } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';
import { usePortalVoicebox, useStaticVoicebox } from 'vet-bones/utils/voicebox';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { voiceboxQueryBuilder } from 'src/constants/voiceboxQueryBuilder';
import { useClickModelConcept } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useClickModelConcept';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useVoiceboxModelingAndMapping } from 'src/containers/ProjectDiagram/hooks/useVoiceboxModelingAndMapping';
import {
  DESIGNER_REQUEST_EXAMPLES,
  DESIGNER_RESPONSE_ACTIONS,
  DESIGNER_RESPONSE_MESSAGES,
} from 'src/containers/ProjectDiagram/utils/__tests__/voicebox.fixtures';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import { hasIri } from 'src/containers/ProjectDiagram/utils/hasIri';
import { projectSelector } from 'src/selectors/project';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { voiceboxCopy, voiceboxTestIds } from 'src/templates/voicebox';
import { exportModel } from 'src/utils/export/model';
import { getProjectTargetModel } from 'src/utils/getProjectTargetModel';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type VoiceboxContextProviderProps = {
  children: React.ReactNode;
};

const VoiceboxContextProvider: React.VFC<VoiceboxContextProviderProps> = ({
  children,
}) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const connectionIdx = getCurrentConnectionInfo(window)?.connectionIndex || 0;
  const portalConnection = useDefaultPortalConnection();
  const connectionId = portalConnection?.id || '';
  const portalFeatures = usePortalFeatures();

  const handleClickModelConcept = useClickModelConcept();
  const onClickIri = React.useCallback(
    (iri: string) => {
      if (!diagram || !hasIri(iri, diagram)) {
        return null;
      }

      return handleClickModelConcept;
    },
    [handleClickModelConcept, diagram]
  );

  const handleToggleDrawer = React.useCallback(
    (isOpen: boolean) => {
      dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isOpen }));
    },
    [dispatch]
  );

  const queryBuilderModel = React.useMemo(
    () => (diagram ? getQueryBuilderModel(diagram.model) : null),
    [diagram]
  );

  const modelNamespace = project
    ? getProjectTargetModel(project).namespace
    : '';
  const modelRdf = React.useMemo(() => {
    if (!project) {
      return '';
    }
    const modelFile = exportModel({
      name: project.name,
      model: project.diagram.model,
      projectPropertyType: project.propertyType,
      targetModelId: project.targetModelId,
    });
    return modelFile.contents;
  }, [project]);

  const schema = React.useMemo(
    (): VoiceboxSchema => ({
      copy: voiceboxCopy,
      testIds: voiceboxTestIds,
      queryBuilder: voiceboxQueryBuilder,
      queryBuilderModel,
      queryBuilderNamespaces: null,

      clipboard: {
        isClipboardEnabled: getIsClipboardEnabled(),
        copyToClipboard,
      },

      config: {
        app: VoiceboxApp.DESIGNER,
        connectionId,
        connectionIdx,
        context: {
          model_namespace: modelNamespace,
          model_rdf: modelRdf,
        },
        defaultSettings: DEFAULT_VOICEBOX_SETTINGS,
        description: copy.components.voicebox.drawer.description,
        examples:
          (portalFeatures.staticVoicebox && DESIGNER_REQUEST_EXAMPLES) ||
          copy.components.voicebox.drawer.examples,
        initialSettings: null,
        initialThinkMode: false,
      },

      drawer: {
        isOpen: true,
        staticHeader: true,
        toggleIsOpen: handleToggleDrawer,
        usePortal: false,
      },

      links: {
        onClickIri,
      },

      settingsAside: {},
    }),
    [
      connectionId,
      connectionIdx,
      handleToggleDrawer,
      modelNamespace,
      modelRdf,
      onClickIri,
      portalFeatures,
      queryBuilderModel,
    ]
  );

  const onFeedbackError = React.useCallback(
    (message: string) => {
      dispatch(
        uiSlice.actions.voiceboxFeedbackFailed({
          message,
        })
      );
    },
    [dispatch]
  );

  const handleModelingAndMapping = useVoiceboxModelingAndMapping();
  const onMessageSuccess = React.useCallback(
    (message: VoiceboxResponseMessage) => {
      dispatch(
        projectsActionCreators.updatedProjectVoiceboxConversation(
          projectId,
          message.conversationId
        )
      );

      if (message.actions) {
        handleModelingAndMapping(message.actions);
      }
    },
    [dispatch, handleModelingAndMapping, projectId]
  );

  const staticVoicebox = useStaticVoicebox({
    id: projectId,
    initialConversationId: project?.voiceboxConversationId,
    key: project?.voiceboxConversationId,
    onMessageError: (e) => console.error(e),
    onMessageSuccess,
    schema,
    staticMessages: project?.voiceboxMessageHistory,
    cloudResponseConfig: {
      responseMessages: DESIGNER_RESPONSE_MESSAGES,
      responseAdditionalActions: DESIGNER_RESPONSE_ACTIONS,
    },
  });
  const portalVoicebox = usePortalVoicebox({
    id: projectId,
    initialConversationId: project?.voiceboxConversationId,
    key: project?.voiceboxConversationId,
    onDownloadCsvError: noop,
    onDownloadCsvSuccess: noop,
    onFeedbackError,
    onMessageError: noop,
    onMessageSuccess,
    schema,
    staticMessages: project?.voiceboxMessageHistory,
  });

  const conversation = portalFeatures.staticVoicebox
    ? staticVoicebox
    : portalVoicebox;

  const voiceboxSettings = useVoiceboxSettingsValue({ conversation, schema });

  return (
    <VoiceboxSchemaContext.Provider value={schema}>
      <VoiceboxConversationContext.Provider value={conversation}>
        <VoiceboxSettingsContext.Provider value={voiceboxSettings}>
          {children}
        </VoiceboxSettingsContext.Provider>
      </VoiceboxConversationContext.Provider>
    </VoiceboxSchemaContext.Provider>
  );
};

type ProjectSidebarVoiceboxContextProviderProps = {
  children: React.ReactNode;
};

export const ProjectSidebarVoiceboxContextProvider: React.VFC<
  ProjectSidebarVoiceboxContextProviderProps
> = ({ children }) => {
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  return isVoiceboxEnabled ? (
    <VoiceboxContextProvider>{children}</VoiceboxContextProvider>
  ) : (
    <>{children}</>
  );
};
